import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import {
  AccountingCustomerRecord,
  AccountingGroupOverviewFilters,
  AccountingGroupOverviewRow,
  AccountingGroupOverviewTotals,
  AccountingReviewAccountLevel,
  AccountingReviewFloatingEntityType,
  BankRecord,
  CashboxRecord,
  FinanceApiService
} from './finance-api.service';

interface SelectOption<T extends string = string> {
  label: string;
  value: T;
}

@Component({
  standalone: true,
  selector: 'ain-finance-accounting-review',
  imports: [
    AinPageComponent,
    AinSearchAccordionComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    JalaliDateInputComponent,
    DecimalPipe
  ],
  template: `
    <ain-page [title]="t('finance.accountingReview.title')" [description]="t('finance.accountingReview.description')">
      <section class="card">
        <ain-search-accordion [title]="t('finance.accountingReview.search')">
          <div class="filter-grid">
            <label class="checkbox-field">
              <p-checkbox [(ngModel)]="includeOpening" [binary]="true" />
              <span>{{ t('finance.accountingReview.filters.opening') }}</span>
            </label>
            <label class="checkbox-field">
              <p-checkbox [(ngModel)]="includeClosing" [binary]="true" />
              <span>{{ t('finance.accountingReview.filters.closing') }}</span>
            </label>
            <label class="checkbox-field">
              <p-checkbox [(ngModel)]="includeManual" [binary]="true" />
              <span>{{ t('finance.accountingReview.filters.manual') }}</span>
            </label>
            <label class="checkbox-field">
              <p-checkbox [(ngModel)]="includeMechanized" [binary]="true" />
              <span>{{ t('finance.accountingReview.filters.mechanized') }}</span>
            </label>

            <ain-date-input [(ngModel)]="startDate" [placeholder]="t('finance.accountingReview.filters.startDate')" />
            <ain-date-input [(ngModel)]="endDate" [placeholder]="t('finance.accountingReview.filters.endDate')" />
            <input pInputText type="text" [(ngModel)]="startCode" [placeholder]="t('finance.accountingReview.filters.startCode')" />
            <input pInputText type="text" [(ngModel)]="endCode" [placeholder]="t('finance.accountingReview.filters.endCode')" />

            <p-select
              [options]="levelOptions"
              [(ngModel)]="level"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('finance.accountingReview.filters.level')" />

            @if (level === 'Floating') {
              <p-select
                [options]="floatingTypeOptions"
                [(ngModel)]="floatingEntityType"
                optionLabel="label"
                optionValue="value"
                [placeholder]="t('finance.accountingReview.filters.floatingEntityType')" />
            }
          </div>

          <div class="actions">
            <button pButton type="button" severity="danger" [label]="t('finance.accountingReview.clear')" (click)="clearFilters()"></button>
            <button pButton type="button" [label]="t('finance.accountingReview.calculate')" (click)="applySearch()"></button>
          </div>
        </ain-search-accordion>

        <p-table
          [value]="displayRows()"
          [paginator]="true"
          [rows]="pageSize"
          [totalRecords]="totalCount()"
          [lazy]="true"
          (onLazyLoad)="onPageChange($event)">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.accountingReview.fields.code') }}</th>
              <th>{{ t('finance.accountingReview.fields.name') }}</th>
              <th>{{ t('finance.accountingReview.fields.debtor') }}</th>
              <th>{{ t('finance.accountingReview.fields.creditor') }}</th>
              <th>{{ t('finance.accountingReview.fields.debtorRemaining') }}</th>
              <th>{{ t('finance.accountingReview.fields.creditorRemaining') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.code }}</td>
              <td>{{ row.name }}</td>
              <td [class.negative]="row.debtor < 0">{{ row.debtor | number:'1.0-0' }}</td>
              <td [class.negative]="row.creditor < 0">{{ row.creditor | number:'1.0-0' }}</td>
              <td [class.nature-warning]="isDebtorRemainingWarning(row)">{{ row.debtorRemaining | number:'1.0-0' }}</td>
              <td [class.nature-warning]="isCreditorRemainingWarning(row)">{{ row.creditorRemaining | number:'1.0-0' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6">{{ t('finance.accountingReview.noRecords') }}</td></tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr class="totals-row">
              <td colspan="2"><strong>{{ t('finance.accountingReview.totals.label') }}</strong></td>
              <td [class.negative]="totals().debtorTotal < 0"><strong>{{ totals().debtorTotal | number:'1.0-0' }}</strong></td>
              <td [class.negative]="totals().creditorTotal < 0"><strong>{{ totals().creditorTotal | number:'1.0-0' }}</strong></td>
              <td><strong>{{ totals().debtorRemainingTotal | number:'1.0-0' }}</strong></td>
              <td><strong>{{ totals().creditorRemainingTotal | number:'1.0-0' }}</strong></td>
            </tr>
          </ng-template>
        </p-table>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; display: grid; gap: 12px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; align-items: center; }
    .checkbox-field { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #334155; }
    .actions { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; }
    .totals-row td { background: #f9fafb; }
    .negative { color: #b91c1c; }
    .nature-warning { color: #b91c1c; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    @media (max-width: 1200px) { .filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class FinanceAccountingReviewComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly pageSize = 25;
  page = 1;

  level: AccountingReviewAccountLevel = 'Group';
  floatingEntityType: AccountingReviewFloatingEntityType = 'Person';
  startCode = '';
  endCode = '';
  startDate = '';
  endDate = '';
  includeOpening = false;
  includeClosing = false;
  includeManual = false;
  includeMechanized = false;

  readonly rows = signal<AccountingGroupOverviewRow[]>([]);
  readonly totalCount = signal(0);
  readonly totals = signal<AccountingGroupOverviewTotals>({
    debtorTotal: 0,
    creditorTotal: 0,
    debtorRemainingTotal: 0,
    creditorRemainingTotal: 0
  });
  readonly errorMessage = signal('');
  readonly floatingLabels = signal<Record<string, string>>({});

  readonly levelOptions: SelectOption<AccountingReviewAccountLevel>[] = [
    { value: 'Group', label: '' },
    { value: 'General', label: '' },
    { value: 'Subsidiary', label: '' },
    { value: 'Detail', label: '' },
    { value: 'Floating', label: '' }
  ];

  readonly floatingTypeOptions: SelectOption<AccountingReviewFloatingEntityType>[] = [
    { value: 'Person', label: '' },
    { value: 'OrganizationUnit', label: '' },
    { value: 'Bank', label: '' },
    { value: 'CashFund', label: '' },
    { value: 'Warehouse', label: '' }
  ];

  readonly displayRows = computed(() => {
    const labels = this.floatingLabels();
    if (this.level !== 'Floating') {
      return this.rows();
    }

    return this.rows().map(row => ({
      ...row,
      name: labels[row.code] ?? row.name
    }));
  });

  ngOnInit(): void {
    this.levelOptions.forEach(option => {
      option.label = this.t(`finance.accountingReview.levels.${option.value}`);
    });
    this.floatingTypeOptions.forEach(option => {
      option.label = this.t(`finance.accountingReview.floatingTypes.${option.value}`);
    });

    this.api.listFiscalPeriods().pipe(catchError(() => of([]))).subscribe(periods => {
      if (!periods.length) {
        return;
      }

      const starts = periods.map(period => period.startDateUtc).filter(Boolean).sort();
      const ends = periods.map(period => period.endDateUtc).filter(Boolean).sort();
      if (starts.length) {
        this.startDate = starts[0].slice(0, 10);
      }
      if (ends.length) {
        this.endDate = ends[ends.length - 1].slice(0, 10);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  applySearch(): void {
    this.page = 1;
    this.load();
  }

  clearFilters(): void {
    this.includeOpening = false;
    this.includeClosing = false;
    this.includeManual = false;
    this.includeMechanized = false;
    this.startCode = '';
    this.endCode = '';
    this.level = 'Group';
    this.floatingEntityType = 'Person';
    this.startDate = '';
    this.endDate = '';
    this.page = 1;
    this.load();
  }

  onPageChange(event: TableLazyLoadEvent): void {
    const nextPage = event.first !== undefined && event.rows ? Math.floor(event.first / event.rows) + 1 : 1;
    this.page = nextPage;
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    const filters = this.buildFilters();

    this.api.listAccountingGroupOverview(filters).subscribe({
      next: response => {
        this.rows.set(response.items);
        this.totalCount.set(response.totalCount);
        this.totals.set(response.totals);
        if (this.level === 'Floating') {
          this.loadFloatingLabels(response.items);
        } else {
          this.floatingLabels.set({});
        }
      },
      error: () => this.errorMessage.set(this.t('finance.accountingReview.loadFailed'))
    });
  }

  isDebtorRemainingWarning(row: AccountingGroupOverviewRow): boolean {
    return row.debtorRemaining > 0 && row.nature === 'Creditor';
  }

  isCreditorRemainingWarning(row: AccountingGroupOverviewRow): boolean {
    return row.creditorRemaining > 0 && row.nature === 'Debtor';
  }

  private buildFilters(): AccountingGroupOverviewFilters {
    return {
      level: this.level,
      floatingEntityType: this.level === 'Floating' ? this.floatingEntityType : undefined,
      startCode: this.startCode.trim() || undefined,
      endCode: this.endCode.trim() || undefined,
      startDateUtc: this.startDate || undefined,
      endDateUtc: this.endDate || undefined,
      includeOpening: this.includeOpening,
      includeClosing: this.includeClosing,
      includeManual: this.includeManual,
      includeMechanized: this.includeMechanized,
      page: this.page,
      pageSize: this.pageSize
    };
  }

  private loadFloatingLabels(rows: AccountingGroupOverviewRow[]): void {
    forkJoin({
      customers: this.api.listAccountingCustomers().pipe(catchError(() => of([] as AccountingCustomerRecord[]))),
      banks: this.api.listAccountingBanks(undefined, undefined, { page: 1, pageSize: 500 }).pipe(catchError(() => of([] as BankRecord[]))),
      cashboxes: this.api.listAccountingCashboxes(undefined, undefined, { page: 1, pageSize: 500 }).pipe(catchError(() => of([] as CashboxRecord[])))
    }).subscribe(({ customers, banks, cashboxes }) => {
      const labels: Record<string, string> = {};

      if (this.floatingEntityType === 'Person') {
        customers.forEach(customer => {
          const id = String(customer.key ?? '');
          if (!id) {
            return;
          }
          labels[id] = customer.displayName || id;
        });
      }

      if (this.floatingEntityType === 'Bank') {
        banks.forEach(bank => {
          labels[bank.id] = `${bank.bankName ?? ''}${bank.accountNumber ? ` - ${bank.accountNumber}` : ''}`.trim() || bank.id;
        });
      }

      if (this.floatingEntityType === 'CashFund') {
        cashboxes.forEach(cashbox => {
          labels[cashbox.id] = cashbox.name || cashbox.id;
        });
      }

      rows.forEach(row => {
        if (!labels[row.code]) {
          labels[row.code] = row.name;
        }
      });

      this.floatingLabels.set(labels);
    });
  }
}
